import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';
import db from '@/db/db';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { discardCaseNoteDraft } from '@/db/services/case_note_service';
import { formatUnitCount, interactionTypeLabel, type CaseNote } from '@/db/types/case_note';
import { useClientFromParam } from '@/hooks/useClientFromParam';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { useServerOnline } from '@/hooks/useServerOnline';
import { describeSyncError } from '@/lib/sync-errors';
import NoteStatusPill from '@/components/notes/NoteStatusPill';
import { formatNoteDate, formatNoteTimestamp } from '@/components/notes/format';
import { noteIdFromParam } from '@/components/notes/route';

/** One note, readable offline. A note still on this device can be edited, retried or discarded. */
export default function NoteDetail() {
  const navigate = useNavigate();
  const { noteId: noteParam } = useParams<{ noteId: string }>();
  const { clientSlug, loading } = useClientFromParam();
  const { currentPerson, organizationId } = useCurrentPerson();
  const online = useServerOnline();
  const noteId = noteIdFromParam(noteParam);

  const note = useLiveQuery(async () => (noteId === null ? null : (await db.caseNotes.get(noteId)) ?? null), [noteId]);
  const options = useLiveQuery(
    async () => (organizationId ? (await db.noteOptions.get(organizationId)) ?? null : null),
    [organizationId],
  );

  const [retrying, setRetrying] = useState(false);
  const [retryOutcome, setRetryOutcome] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const notesTab = `/clients/${clientSlug ?? ''}?tab=notes`;

  if (loading || note === undefined) return <div className="h-full bg-gray-50" />;

  if (!note) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Note not found</h2>
          <p className="text-gray-600 mb-4">This note isn&apos;t on this device. It may have uploaded under a new link.</p>
          <Button variant="secondary" onClick={() => navigate(notesTab)}>Back to notes</Button>
        </div>
      </div>
    );
  }

  const editPath = `/clients/${clientSlug}/notes/${note.id}/edit`;
  const refused = !note.synced && Boolean(note.sync_error_message);
  const units = note.service_units.filter(unit => Number(unit.unit_count) > 0);
  const subject = note.note_subject?.name ?? note.subject;
  const interaction = interactionTypeLabel(note, options);
  const writtenByMe = note.author?.id != null && note.author.id === currentPerson?.id;

  const retry = async () => {
    setRetrying(true);
    setRetryOutcome(null);
    try {
      const result = await dispatchDataEvent<typeof DataEvents.SYNC_CASE_NOTE, CaseNote | null>(DataEvents.SYNC_CASE_NOTE, { noteId: note.id });
      if (result?.synced) {
        navigate(`/clients/${clientSlug}/notes/${result.id}`, { replace: true });
      } else if (result?.sync_error_message) {
        setRetryOutcome('Mon Ami refused it again. Edit the note to fix what it names above.');
      }
    } catch (error) {
      setRetryOutcome(describeSyncError(error));
    } finally {
      setRetrying(false);
    }
  };

  const discard = async () => {
    await discardCaseNoteDraft(note.id);
    navigate(notesTab, { replace: true });
  };

  return (
    <div className="h-full bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Button variant="tertiary" size="sm" icon="caret-left" onClick={() => navigate(notesTab)}>Notes</Button>
          <h1 className="text-base font-semibold text-gray-900">{note.type === 'CaseNote' ? 'Case note' : 'Basic note'}</h1>
          {!note.synced ? (
            <Button variant="secondary" size="sm" icon="pencil" onClick={() => navigate(editPath)}>Edit</Button>
          ) : <span className="w-12" aria-hidden="true" />}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4" data-testid="note-detail">
        {refused && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3 text-red-800" role="alert">
            <p className="text-sm font-semibold">Mon Ami didn&apos;t accept this note.</p>
            <p className="text-sm">{note.sync_error_message} Your note is still saved on this device.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" icon="pencil" onClick={() => navigate(editPath)}>Edit note</Button>
              <Button variant="tertiary" size="sm" icon="arrow-rotate-left" loading={retrying} disabled={!online || retrying} onClick={() => void retry()}>
                {retrying ? 'Trying…' : 'Try again'}
              </Button>
            </div>
            {retryOutcome && <p className="text-xs" role="status">{retryOutcome}</p>}
          </div>
        )}

        {!note.synced && !refused && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            Saved on this device. It uploads to Mon Ami when you&apos;re online.
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-2xl text-black">{formatNoteDate(note.interaction_date)}</h2>
          <NoteStatusPill note={note} />
        </div>

        <dl className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2 text-sm">
          {interaction && (<><dt className="text-gray-500">{note.type === 'BasicNote' ? 'Source' : 'Coding'}</dt><dd className="text-gray-900">{interaction}</dd></>)}
          {note.program && (<><dt className="text-gray-500">Program</dt><dd className="text-gray-900">{note.program.name}</dd></>)}
          {note.site && (<><dt className="text-gray-500">Site</dt><dd className="text-gray-900">{note.site.name}</dd></>)}
          {subject && (<><dt className="text-gray-500">Subject</dt><dd className="text-gray-900">{subject}</dd></>)}
          {note.check_in && (<><dt className="text-gray-500">Check-in</dt><dd className="text-gray-900 capitalize">{note.check_in}</dd></>)}
        </dl>

        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-line">{note.detail}</div>

        {units.length > 0 && (
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Service units</h3>
            <ul className="divide-y divide-gray-100 bg-white border border-gray-200 rounded-lg">
              {units.map(unit => (
                <li key={unit.service_definition_id} className="flex justify-between gap-3 px-4 py-2 text-sm">
                  <span className="text-gray-900">{unit.name}{unit.funding_source && <span className="text-gray-500"> · {unit.funding_source.name}</span>}</span>
                  <span className="text-gray-700 shrink-0">{formatUnitCount(unit)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-xs text-gray-500">
          {note.synced
            ? <>{note.author?.name ?? 'Unknown author'} · {formatNoteTimestamp(note.created_at)}</>
            : <>Written by {writtenByMe ? 'you' : note.author?.name ?? 'you'} on this device · {formatNoteTimestamp(note.created_at)}</>}
        </p>

        {note.synced && (
          <div className="bg-[#E6F0F0] text-deep-sea-green rounded-lg px-4 py-3 text-sm">To change this note, open the client in Mon Ami.</div>
        )}

        {!note.synced && (
          <div className="pt-2">
            {confirmingDiscard ? (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-900">Discard this note? It hasn&apos;t uploaded, so it will be gone for good.</p>
                <div className="flex gap-2">
                  <Button variant="primary" size="sm" destructive onClick={() => void discard()}>Discard note</Button>
                  <Button variant="tertiary" size="sm" onClick={() => setConfirmingDiscard(false)}>Keep it</Button>
                </div>
              </div>
            ) : (
              <Button variant="borderless" size="sm" icon="trash" destructive onClick={() => setConfirmingDiscard(true)}>Discard note</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
